import { ApolloError, type MutationHookOptions, type TypedDocumentNode } from '@apollo/client';
import { Button, Typography } from '@mui/material';
import type { GraphQLError } from 'graphql';
import { useSnackbar } from 'notistack';
import { useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

export interface PersistentSnackbarOptions {
  /** Offers a page reload beside Dismiss, for failures that may have left stale state behind. */
  reload?: boolean;
  variant?: 'error' | 'warning';
}

/**
 * A toast that stays until the user dismisses it, for failures they have to act on. The single
 * home of that pattern: mutation errors reach it through `useNotifications`.
 */
export const usePersistentSnackbar = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { t } = useTranslation();
  return useCallback(
    (message: ReactNode, { reload = false, variant = 'error' }: PersistentSnackbarOptions = {}) => {
      const key = uuid();
      enqueueSnackbar(<Typography color="inherit">{message}</Typography>, {
        action: (
          <>
            {reload && (
              <Button color="inherit" onClick={() => window.location.reload()}>
                {t('api.common.reload')}
              </Button>
            )}
            <Button color="inherit" onClick={() => closeSnackbar(key)}>
              {t('api.common.dismiss')}
            </Button>
          </>
        ),
        key,
        persist: true,
        variant,
      });
    },
    [closeSnackbar, enqueueSnackbar, t],
  );
};

export type ApiOptions<T> = T extends TypedDocumentNode<infer M, infer V>
  ? MutationHookOptions<M, V>
  : never;

export interface NotificationOptions<T> {
  failure?: string | ((error: ApolloError) => ReactNode) | undefined;
  success?: string | ((data: T) => ReactNode) | undefined;
}

export const useNotifications = <T, U>(
  _document: TypedDocumentNode<T, U>,
  { failure, success }: NotificationOptions<T>,
) => {
  const { enqueueSnackbar } = useSnackbar();
  const persistentSnackbar = usePersistentSnackbar();

  const onCompleted =
    success == null
      ? undefined
      : (data: T) => {
          const message = typeof success === 'string' ? success : success(data);
          enqueueSnackbar(<Typography color="inherit">{message}</Typography>, {
            variant: 'success',
          });
        };

  const onError =
    failure == null
      ? undefined
      : (error: ApolloError) => {
          const message = typeof failure === 'string' ? failure : failure(error);
          persistentSnackbar(message, { reload: true });
        };

  return { onCompleted, onError };
};

interface ServerFieldError {
  explanation?: string[] | string;
  path?: string[];
}

/** What a mutate call resolves to. With an onError handler Apollo hands the ApolloError back as `errors`. */
interface MutationResult {
  errors?: ApolloError | readonly GraphQLError[] | undefined;
}

/**
 * Puts a failed mutation's field errors on the form so they show beside their inputs. The schema
 * attaches them to a RecordInvalid failure as `extensions.errors[{ path, explanation }]`;
 * `fieldMap` renames server attributes whose form field is called something else (deviceGroupId
 * to deviceGroup). Errors on `base`, or on attributes with no field, stay with the toast.
 * Returns whether anything was put on the form.
 */
export const applyServerErrors = (
  result: MutationResult | null | undefined,
  setErrors: (errors: Record<string, string>) => void,
  fieldMap: Record<string, string> = {},
): boolean => {
  const errors = result?.errors;
  const graphQLErrors = errors instanceof ApolloError ? errors.graphQLErrors : errors ?? [];
  const mapped: Record<string, string> = {};
  for (const { extensions } of graphQLErrors) {
    if (!Array.isArray(extensions.errors)) continue;
    for (const { path, explanation } of extensions.errors as ServerFieldError[]) {
      const attribute = path?.[0];
      if (!attribute || attribute === 'base' || !explanation) continue;
      const field = attribute in fieldMap ? fieldMap[attribute] : attribute;
      const text = [explanation].flat().join(' ');
      mapped[field] = mapped[field] ? `${mapped[field]} ${text}` : text;
    }
  }
  if (Object.keys(mapped).length === 0) return false;
  setErrors(mapped);
  return true;
};
